import SimpleEmitter from './SimpleEmitter.js';
import {
  checkAccessKey,
  isObject,
  parseOrigin,
  asyncSleep,
  defer,
} from './utils.js';

const allowedLocales = ['en', 'de', 'fr', 'ru'];
const sendMessageTimeout = 2000;
const iframeLoadTimeout = 10000;

/**
 * @typedef {Object} EyesonIframeGuestOptions
 * @prop {string} token
 * @prop {string} [locale]
 * @prop {boolean} [suggestName]
 *
 * @typedef {Object} EyesonIframeEvents
 * @prop {Function} [onReady]
 * @prop {Function} [onError]
 * @prop {Function} [onClosed]
 * @prop {Function} [onStatusChange]
 * @prop {Function} [onAuthenticated]
 * @prop {Function} [onFullscreen]
 * @prop {Function} [onMeetingJoin]
 * @prop {Function} [onMeetingLeave]
 * @prop {Function} [onMeetingError]
 * @prop {Function} [onUserChange]
 *
 * @typedef {Object} EyesonIframeOptions
 * @prop {string} [accessKey]
 * @prop {EyesonIframeGuestOptions} [guest]
 * @prop {string|number} [width]
 * @prop {string|number} [height]
 * @prop {string} [css]
 * @prop {string} [title]
 * @prop {string} [className]
 * @prop {boolean} [isPermalink]
 * @prop {EyesonIframeEvents} [events]
 */

class EyesonIframe extends SimpleEmitter {
  /**
   * EyesonIframe
   * @see https://docs.eyeson.com/docs/iframe/references/constructor
   * @param {String|Node|HTMLElement} selector - Container element or selector string
   * @param {EyesonIframeOptions} options - { accessKey ( or guest.token required!), guest: { token, locale, suggestName }, width, height, css, title, className, events }
   * @returns {EyesonIframe}
   */
  constructor(selector, options) {
    super();
    let _status = '';
    let _element = null;
    let _hasGuestToken = false;
    if (typeof selector === 'string') {
      _element =
        document.querySelector(selector) || document.getElementById(selector);
    } else if (selector instanceof HTMLElement) {
      _element = selector;
    }
    if (!_element) {
      throw new TypeError('Invalid element', selector);
    }
    if (!isObject(options)) {
      throw new TypeError('Missing options object');
    }
    if (isObject(options.guest)) {
      _hasGuestToken =
        typeof options.guest.token === 'string' && options.guest.token !== '';
    }
    if (!(options.accessKey || _hasGuestToken)) {
      throw new TypeError('Missing access key or guest token');
    }
    /** @private */
    this._element = _element;
    /** @private */
    this._options = options;
    Object.defineProperty(this, 'status', {
      get: () => _status,
      set: status => {
        _status = status;
        this._emit('statuschange', _status);
      },
      enumerable: true,
      configurable: true,
    });
    this.features = {
      present: false,
      chat: false,
      snapshot: false,
    };
    /** @private */
    this._channel = null;
    /** @private */
    this._iframe = null;
    /** @private */
    this._storage = [];
    /** @private */
    this._refTimer = null;
    /** @private */
    this._loadTimer = null;
    /** @private */
    this._authenticated = false;
    /** @private */
    this._inMeeting = false;
    /** @private */
    this._origin = parseOrigin(options.origin) || 'https://app.eyeson.team';
    /** @private */
    this._basePath = options.basePath || '/';
    applyEvents(this, options.events);
    this.status = 'init';
    if (
      (!_hasGuestToken && !checkAccessKey(options.accessKey)) ||
      (_hasGuestToken && !checkAccessKey(options.guest.token))
    ) {
      setTimeout(() => {
        this.status = 'error';
        this._emit(
          'error',
          new TypeError(
            `Invalid ${_hasGuestToken ? 'guest token' : 'access key'}`,
          ),
        );
      });
      return this;
    }
    const readyPromise = defer();
    initIframe(this, readyPromise);
    /** @private */
    this._listener = applyListener(this, readyPromise);
    readyPromise.promise.then(
      () => {
        this.status = 'ready';
        this._emit('ready');
      },
      error => {
        this.status = 'error';
        this._emit('error', error);
      },
    );
  }

  /**
   * Current version number
   * @returns {string} - Version number
   */
  static get version() {
    return '1.2.0';
  }

  /**
   * Present one or more files. Can be image(s) or PDF.
   * @param {Blob|Array<Blob>|FileList} files - Blob or array of blobs of image(s) or PDF
   * @returns {Promise}
   */
  present(files) {
    if (!this._authenticated) {
      return Promise.reject(
        new DOMException('Authentication error', 'NotAllowedError'),
      );
    }
    if (!this.features.present) {
      return Promise.reject(
        new DOMException('Not available', 'NotAllowedError'),
      );
    }
    if (!this._inMeeting) {
      return Promise.reject(
        new DOMException('Invalid state', 'InvalidStateError'),
      );
    }
    if (files instanceof Blob) {
      files = [files];
    }
    if (Object.prototype.toString.call(files) === '[object FileList]') {
      files = Array.from(files);
    }
    if (!Array.isArray(files) || files.length === 0) {
      return Promise.reject(new TypeError('Invalid files argument'));
    }
    return sendMessagePromise(this, { type: 'present', files });
  }

  /**
   * Present PDF document
   * @param {Blob} file - Blob of type 'application/pdf'
   * @returns {Promise}
   */
  presentDocument(file) {
    if (!this._authenticated) {
      return Promise.reject(
        new DOMException('Authentication error', 'NotAllowedError'),
      );
    }
    if (!this.features.present) {
      return Promise.reject(
        new DOMException('Not available', 'NotAllowedError'),
      );
    }
    if (!this._inMeeting) {
      return Promise.reject(
        new DOMException('Invalid state', 'InvalidStateError'),
      );
    }
    if (!(file instanceof Blob)) {
      return Promise.reject(new TypeError('Invalid file argument'));
    }
    return sendMessagePromise(this, { type: 'present', file });
  }

  /**
   * Send chat message
   * @param {string} message - Chat message
   * @returns {Promise}
   */
  chat(message) {
    if (!this._authenticated) {
      return Promise.reject(
        new DOMException('Authentication error', 'NotAllowedError'),
      );
    }
    if (!this.features.chat) {
      return Promise.reject(
        new DOMException('Not available', 'NotAllowedError'),
      );
    }
    if (!this._inMeeting) {
      return Promise.reject(
        new DOMException('Invalid state', 'InvalidStateError'),
      );
    }
    if (typeof message !== 'string') {
      return Promise.reject(new TypeError('Invalid message type'));
    }
    return sendMessagePromise(this, { type: 'chat', message });
  }

  /**
   * Create snapshot
   * @returns {Promise}
   */
  snapshot() {
    if (!this._authenticated) {
      return Promise.reject(
        new DOMException('Authentication error', 'NotAllowedError'),
      );
    }
    if (!this.features.snapshot) {
      return Promise.reject(
        new DOMException('Not available', 'NotAllowedError'),
      );
    }
    if (!this._inMeeting) {
      return Promise.reject(
        new DOMException('Invalid state', 'InvalidStateError'),
      );
    }
    return sendMessagePromise(this, { type: 'snapshot' });
  }

  async reload() {
    if (this._iframe) {
      if (this._channel && !this._channel.closed) {
        sendMessage(this, { type: 'close' });
        await asyncSleep(100);
      }
      this._iframe.src = this._iframe.src;
    }
  }

  /**
   * Get injected iframe element
   * @returns {null|HTMLIFrameElement}
   */
  getIFrame() {
    return this._iframe;
  }

  /**
   * Check current authentication state
   * @returns {boolean} isAuthenticated
   */
  isAuthenticated() {
    return this._authenticated;
  }

  /**
   * Get current user meeting state
   * @returns {boolean|null} isInMeeting
   */
  isInMeeting() {
    return this._authenticated ? this._inMeeting : null;
  }

  /**
   * Destroy EyesonIframe instance, cleanup and remove injected iframe element
   * @returns {Promise}
   */
  async close() {
    clearTimeout(this._loadTimer);
    if (this._channel && !this._channel.closed) {
      sendMessage(this, { type: 'close' });
      await asyncSleep(100);
    }
    this.status = 'closed';
    this._emit('closed');
    this.off();
    this._channel = null;
    clearInterval(this._refTimer);
    if (this._element && this._iframe) {
      try {
        this._element.removeChild(this._iframe);
        while (this._storage.length > 0) {
          this._element.appendChild(this._storage.shift());
        }
      } catch (e) {}
    }
    if (this._listener) {
      window.removeEventListener('message', this._listener);
      this._listener = null;
    }
    this._storage.length = 0;
    this._iframe = null;
    this._element = null;
    Object.keys(this.features).forEach(key => (this.features[key] = false));
  }
}

const sendMessagePromise = (that, message) => {
  return new Promise((resolve, reject) => {
    let timeout = null;
    const cb = ({ data, origin }) => {
      if (
        origin !== that._origin ||
        data.type !== 'response' ||
        data.action !== message.type
      ) {
        return;
      }
      clearTimeout(timeout);
      window.removeEventListener('message', cb);
      if (data.status === 'success') {
        resolve();
      } else {
        reject(data.reason);
      }
    };
    window.addEventListener('message', cb);
    sendMessage(that, message);
    timeout = setTimeout(() => {
      window.removeEventListener('message', cb);
      reject(new DOMException('Message timeout', 'TimeoutError'));
    }, sendMessageTimeout);
  });
};

const sendMessage = (that, message) => {
  if (that._channel && !that._channel.closed) {
    that._channel.postMessage(message, that._origin);
  }
};

const initIframe = (that, readyPromise) => {
  let features = [
    'camera',
    'microphone',
    'fullscreen',
    'speaker-selection',
    'display-capture',
    'picture-in-picture',
    'web-share',
    'autoplay',
    'hid',
    'clipboard-write',
    'geolocation',
    'compute-pressure',
  ];
  const iframe = document.createElement('iframe');
  const supportsFeaturePolicy = typeof iframe.featurePolicy !== 'undefined';
  if (supportsFeaturePolicy) {
    const available = iframe.featurePolicy.features();
    features = features.filter(feature => available.includes(feature));
  }
  iframe.title = that._options.title || 'Eyeson meeting';
  iframe.allow = features.join(';');
  if (!(supportsFeaturePolicy && features.includes('fullscreen'))) {
    iframe.allowFullscreen = true;
  }
  iframe.onload = () => {
    that._loadTimer = setTimeout(() => {
      readyPromise.reject(new DOMException('Loading timeout', 'TimeoutError'));
    }, iframeLoadTimeout);
  };
  const url = generateURL(that);
  if (!url) {
    readyPromise.reject(
      new DOMException('Initialization failed', 'NotFoundError'),
    );
    return;
  }
  iframe.src = url;
  const width = Reflect.has(that._options, 'width')
    ? Number.isInteger(that._options.width)
      ? `${that._options.width}px`
      : that._options.width
    : '100%';
  const height = Reflect.has(that._options, 'height')
    ? Number.isInteger(that._options.height)
      ? `${that._options.height}px`
      : that._options.height
    : '100%';
  iframe.style.cssText =
    typeof that._options.css === 'string'
      ? that._options.css
      : `display:block;width:${width};height:${height};border:none`;
  if (typeof that._options.className === 'string') {
    iframe.className = that._options.className;
  }
  while (that._element.firstChild) {
    that._storage.push(that._element.firstChild);
    that._element.removeChild(that._element.firstChild);
  }
  that._iframe = iframe;
  that._element.appendChild(iframe);
};

const generateURL = ({ _options, _origin, _basePath }) => {
  try {
    const path = _options.isPermalink === true ? 'permalink' : '';
    if (_options.accessKey && checkAccessKey(_options.accessKey)) {
      const url = new URL(`${_basePath}${path}?${_options.accessKey}`, _origin);
      return url.toString();
    }
    if (
      isObject(_options.guest) &&
      _options.guest.token &&
      checkAccessKey(_options.guest.token)
    ) {
      const url = new URL(`${_basePath}${path}`, _origin);
      url.searchParams.set('guest', _options.guest.token);
      if (_options.guest.suggestName === false) {
        url.searchParams.set('suggest', 'false');
      }
      if (allowedLocales.includes(_options.guest.locale)) {
        url.searchParams.set('locale', _options.guest.locale);
      }
      return url.toString();
    }
  } catch (error) {
    console.error('EyesonIframe::generateURL', error);
  }
  return false;
};

const checkReference = that => {
  that._refTimer = setInterval(() => {
    if (!that._channel) {
      clearInterval(that._refTimer);
      return;
    }
    if (that._channel.closed) {
      clearInterval(that._refTimer);
      that.close();
    }
  }, 1000);
};

const applyEvents = (that, events) => {
  if (!isObject(events)) {
    return;
  }
  Object.keys(events).forEach(key => {
    if (key.startsWith('on') && typeof events[key] === 'function') {
      const type = key.substring(2).toLowerCase();
      that.on(type, events[key]);
    }
  });
};

const applyListener = (that, readyPromise) => {
  const listener = ({ data, origin, source }) => {
    if (origin !== that._origin) {
      return;
    }
    if (data.type === 'join') {
      that._inMeeting = true;
      that._emit('meetingjoin');
    } else if (data.type === 'leave') {
      const wasInMeeting = that._inMeeting;
      that._inMeeting = false;
      that._emit(wasInMeeting ? 'meetingleave' : 'meetingerror', data.reason);
    } else if (data.type === 'authenticated') {
      that._authenticated = true;
      Object.assign(that.features, data.features);
      that._emit('authenticated');
    } else if (data.type === 'fullscreen') {
      that._emit('fullscreen', data.isFullscreen);
    } else if (data.type === 'userchange') {
      that.accessKey = data.user.accessKey;
      that._emit('userchange', data.user);
    } else if (data.type === 'hello') {
      const initObject = { type: 'init', token: null };
      if (that._options.accessKey && checkAccessKey(that._options.accessKey)) {
        initObject.token = that._options.accessKey;
      }
      if (that._channel === source) {
        const wasInMeeting = that._inMeeting;
        that._inMeeting = false;
        sendMessagePromise(that, initObject).then(
          () => {
            if (that._authenticated || !wasInMeeting) {
              that._emit(
                wasInMeeting ? 'meetingleave' : 'meetingerror',
                'reload',
              );
            }
            that.status = 'ready';
            that._emit('ready');
          },
          error => {
            console.error('EyesonIframe::reload', error);
            that._channel = null;
            that.status = 'error';
            that._emit(
              'error',
              new DOMException('Initialization failed', 'NotFoundError'),
            );
          },
        );
        return;
      }
      that._channel = source;
      sendMessagePromise(that, initObject).then(
        () => {
          readyPromise.resolve();
          checkReference(that);
        },
        error => {
          console.error('EyesonIframe::init', error);
          that._channel = null;
          readyPromise.reject(
            new DOMException('Initialization failed', 'NotFoundError'),
          );
        },
      );
    }
  };
  window.addEventListener('message', listener);
  return listener;
};

export default EyesonIframe;
